/**
 * The geometry behind the 3D cross-section overlay (spec §11.2).
 *
 * **No three.js import here on purpose.** Everything in this file is
 * arithmetic on ROWS the caller has already picked — a true station line's or
 * a bin mean's, whichever the run has — so it is unit-tested in a plain
 * jsdom run; the r3f component (`components/3D/StationSectionOverlay.tsx`)
 * turns the flat arrays into buffers. That is the same split the bands use
 * (`bandGeometry.ts` / `StationBands.tsx`).
 */
import { pointAtStation, type Alignment } from '@/core/alignment';

import { nearestOffsetIndex, offsetCentre } from './sectionModel';
import type { SeriesRole } from './sectionSeries';

/** Camera distance as a multiple of the slab's largest extent (spec §11.3). */
const LOOK_AT_FRAMING = 1.6;
/** …lifted by this fraction of the distance, so the section is not edge-on. */
const LOOK_AT_LIFT = 0.15;

export interface SectionOverlayAnchors {
  station: [number, number, number];
  left: [number, number, number];
  right: [number, number, number];
  areas: [number, number, number];
}

export interface SectionOverlayRibbon {
  positions: Float32Array;
  colors: Float32Array;
  /**
   * `(offset, elevation)` in METRES, both relative to the same origin as
   * `positions`, for the hatch texture the bands use (`createHatchTexture`).
   * The offset axis is mirrored on cut columns, which turns the one 45°
   * stripe tile into a 135° one — spec §11.2's two hatch directions without a
   * second canvas. The component sets the stripe pitch through
   * `texture.repeat`, sized from `dzMinM`/`dzMaxM` (ruling D38).
   */
  uv: Float32Array;
  index: Uint32Array;
  /**
   * `dz = zTop − zBottom` over the columns actually drawn — the ribbon's own
   * thickness, which is what the hatch pitch has to be sized against (D38).
   * A corridor-sized pitch left every column sampling a single texel, so the
   * "hatch" came out as a flat per-column alpha.
   */
  dzMinM: number;
  dzMaxM: number;
}

export interface SectionOverlayGeometry {
  /** One entry per measured run; each is a flat [x,y,z, …] relative to `origin`. */
  topRuns: Float32Array[];
  bottomRuns: Float32Array[];
  /** Two triangles per measured column, coloured by the sign of dz. */
  ribbon: SectionOverlayRibbon | null;
  /** Every other drawn series — the rest of a stack, the design lines, the overlays. */
  extraRuns: { key: string; role: SeriesRole; color?: string; runs: Float32Array[] }[];
  anchors: SectionOverlayAnchors;
}

/**
 * One sRGB channel byte -> the LINEAR value a `color` BufferAttribute must
 * carry.
 *
 * three's `ColorManagement` is enabled by default under r3f, and a vertex
 * colour attribute is assumed to be in the working (Linear-sRGB) space
 * already — only `THREE.Color` applies the transfer. Handing the raw sRGB
 * bytes over rendered `--sv-cut` `#e34948` as a washed-out pink that neither
 * matched the corridor bands drawn a metre away nor the dock's own swatch.
 * This is three's own `SRGBToLinear`, kept here so the module stays
 * three-free; `sectionOverlay.test.ts` pins it against `new THREE.Color(…)`.
 */
const srgbToLinear = (c: number): number =>
  c < 0.04045 ? c * 0.0773993808 : Math.pow(c * 0.9478672986 + 0.0521327014, 2.4);

/** '#rrggbb' -> linear [r, g, b]. Throws on anything else (ruling G6: no
 *  swallowed errors — a NaN colour silently blanks the ribbon). */
const hexRgbLinear = (hex: string): [number, number, number] => {
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) {
    throw new Error(`sectionOverlayGeometry: expected a #rrggbb colour, got "${hex}"`);
  }
  const n = parseInt(hex.slice(1), 16);
  return [srgbToLinear(((n >> 16) & 255) / 255), srgbToLinear(((n >> 8) & 255) / 255), srgbToLinear((n & 255) / 255)];
};

/** The offset grid every row in one call is sampled on. */
export interface SectionOverlayAxis {
  offsetStartM: number;
  offsetStepM: number;
  nOffsets: number;
}

/**
 * One extra series to draw as a plain polyline — every as-built level outside
 * the ribbon's pair, every design plane, every overlay reference. `z` is one
 * station line's elevations on `axis`, NaN for no data, exactly like the pair.
 */
export interface SectionOverlayLine {
  key: string;
  role: SeriesRole;
  z: Float32Array;
  color?: string;
}

/**
 * The walked station's cross-section, as world geometry on the alignment.
 *
 * The caller picks the ROWS (`zTop`/`zBottom` for the ribbon, `extra` for
 * everything else) and the axis they were sampled on — a true station line's
 * rows on a run that has them, the bin means otherwise. This module never
 * indexes a plane itself, so the 3D drawing follows whatever the 2D section
 * decided to draw (section-overlays spec §2.2).
 *
 * Everything is returned RELATIVE TO `origin` (the alignment's first vertex),
 * because UTM coordinates in Float32 jitter visibly — the component renders
 * it inside a `<group position={origin}>` exactly like the bands do.
 *
 * A no-data column breaks the surface polylines (a bridged gap is a drawing
 * of data that was never measured) and is skipped by the ribbon. Returns
 * `null` only when NOTHING — pair or extra — has a measured column.
 */
export function sectionOverlayGeometry({
  zTop,
  zBottom,
  extra,
  axis,
  alignment,
  station,
  origin,
  swathLeftM,
  swathRightM,
  cut,
  fill,
}: {
  zTop: Float32Array;
  zBottom: Float32Array;
  extra: readonly SectionOverlayLine[];
  axis: SectionOverlayAxis;
  alignment: Alignment;
  station: number;
  origin: [number, number, number];
  swathLeftM: number;
  swathRightM: number;
  cut: string;
  fill: string;
}): SectionOverlayGeometry | null {
  const row = { zTop, zBottom };
  // The three axis fields are all the body below reads; keeping the old name
  // keeps the column arithmetic literally unchanged.
  const offsets = axis;
  const p = pointAtStation(alignment, station);
  // LEFT normal, matching `projectPoint`'s sign convention: offset < 0 is
  // left, so a column at offset o sits at p + n · (−o) … which is the same as
  // p − n · o with n = (−ty, tx). Written as `nx = -p.ty` so the reader can
  // check it against alignment.ts without re-deriving a sign.
  const nx = -p.ty;
  const ny = p.tx;
  const ox = p.x - origin[0];
  const oy = p.y - origin[1];
  const at = (o: number, z: number): [number, number, number] => [ox + nx * -o, oy + ny * -o, z - origin[2]];

  // The SAME column centre the 2D section draws (`offsetCentre`, ruling D22a):
  // the worker bins with `floor((offset + left) / step)`, so column j covers
  // [start + j·step, start + (j+1)·step) and its sample sits in the middle.
  // 2D and 3D therefore agree column for column.
  const columnOffset = (j: number) => offsetCentre(offsets.offsetStartM, offsets.offsetStepM, j);

  const runs = (values: Float32Array): Float32Array[] => {
    const out: Float32Array[] = [];
    let run: number[] = [];
    for (let j = 0; j < offsets.nOffsets; j++) {
      if (Number.isFinite(values[j])) {
        run.push(...at(columnOffset(j), values[j]));
      } else if (run.length) {
        out.push(Float32Array.from(run));
        run = [];
      }
    }
    if (run.length) out.push(Float32Array.from(run));
    return out;
  };

  const topRuns = runs(row.zTop);
  const bottomRuns = runs(row.zBottom);
  // Every other series rides the SAME `runs`, so a design line and an overlay
  // sit on the columns the pair sits on and break at the same no-data gaps.
  const extraRuns = extra.map((line) => ({ key: line.key, role: line.role, color: line.color, runs: runs(line.z) }));
  if (topRuns.length === 0 && bottomRuns.length === 0 && extraRuns.every((e) => e.runs.length === 0)) return null;

  // Ribbon: one quad per column that has BOTH surfaces. A quad is drawn at
  // the column's full width so neighbouring quads meet with no seam.
  const cutRgb = hexRgbLinear(cut);
  const fillRgb = hexRgbLinear(fill);
  const positions: number[] = [];
  const colors: number[] = [];
  const uv: number[] = [];
  const index: number[] = [];
  const half = offsets.offsetStepM / 2;
  let dzMinM = Infinity;
  let dzMaxM = -Infinity;
  for (let j = 0; j < offsets.nOffsets; j++) {
    const t = row.zTop[j];
    const b = row.zBottom[j];
    if (!Number.isFinite(t) || !Number.isFinite(b)) continue;
    const o = columnOffset(j);
    const base = positions.length / 3;
    // dz = zTop − zBottom, dz > 0 -> fill (Global Constraints).
    const dz = t - b;
    const isFill = dz > 0;
    dzMinM = Math.min(dzMinM, dz);
    dzMaxM = Math.max(dzMaxM, dz);
    // Emitted low edge first, high edge second — NOT top-surface first: on a
    // cut column the top surface is physically below the bottom one, so a
    // t/b traversal would wind that quad the opposite way from its fill
    // neighbour. Ordering by elevation makes the winding uniform across the
    // ribbon, which keeps `FrontSide` (and any later outline pass) an option.
    const zLo = Math.min(t, b);
    const zHi = Math.max(t, b);
    for (const [oo, z] of [
      [o - half, zLo],
      [o + half, zLo],
      [o + half, zHi],
      [o - half, zHi],
    ] as const) {
      positions.push(...at(oo, z));
      // Mirroring u on cut columns is what makes the shared stripe tile run
      // the other way — see `SectionOverlayRibbon.uv`. `v` is origin-relative
      // like the positions, so the tile does not depend on the site's height
      // above the ellipsoid.
      uv.push(isFill ? oo : -oo, z - origin[2]);
    }
    const rgb = isFill ? fillRgb : cutRgb;
    for (let k = 0; k < 4; k++) colors.push(rgb[0], rgb[1], rgb[2]);
    index.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }

  // A label's elevation comes from whichever surface WAS measured in that
  // column — `zTop` first, then `zBottom`. A run can legitimately carry only
  // the lower surface, and on a flat GeoJSON alignment the centreline's own
  // `p.z` is unrelated to the ground (spec §4d), so it is the last resort.
  const measuredAt = (j: number): number | null => {
    if (Number.isFinite(row.zTop[j])) return row.zTop[j];
    if (Number.isFinite(row.zBottom[j])) return row.zBottom[j];
    return null;
  };
  const zAt = (j: number) => measuredAt(j) ?? p.z;
  const firstMeasured = () => {
    for (let j = 0; j < offsets.nOffsets; j++) if (measuredAt(j) != null) return j;
    return 0;
  };
  const lastMeasured = () => {
    for (let j = offsets.nOffsets - 1; j >= 0; j--) if (measuredAt(j) != null) return j;
    return offsets.nOffsets - 1;
  };
  // The STA and end-area chips sit ON the centreline, so they take their
  // elevation from the CENTRELINE column — the leftmost measured one is a
  // crossfall away on a crowned or superelevated section, which floated the
  // chips off the drawing they annotate.
  const centreColumn = nearestOffsetIndex(0, offsets.offsetStartM, offsets.offsetStepM, offsets.nOffsets);
  const zCentre = measuredAt(centreColumn) ?? zAt(firstMeasured());

  return {
    topRuns,
    bottomRuns,
    ribbon: index.length
      ? {
          positions: Float32Array.from(positions),
          colors: Float32Array.from(colors),
          uv: Float32Array.from(uv),
          index: Uint32Array.from(index),
          dzMinM,
          dzMaxM,
        }
      : null,
    extraRuns,
    anchors: {
      station: at(0, zCentre),
      left: at(-swathLeftM, zAt(firstMeasured())),
      right: at(swathRightM, zAt(lastMeasured())),
      areas: at(0, zCentre),
    },
  };
}

/** End areas for the overlay's chip; `null` is "not measured", never `0`. */
export interface SectionChipAreas {
  cutM2: number | null;
  fillM2: number | null;
}

/**
 * WHICH end areas the 3D chip prints — the same switch the 2D readout makes
 * (`areasAt` vs `areas` in `StationSectionChart.tsx`).
 *
 * A chip sitting on a true station LINE must print that line's own end areas.
 * `StationBin.cutAreaM2` is the mean over the whole INTERVAL: a different,
 * equally correct quantity, and printing it beside a drawing of one chainage
 * misdescribes the drawing (data honesty). Only when the run has no station
 * lines at all — and the overlay is therefore drawing the bin means — is the
 * bin the right subject.
 *
 * `line.valid === false` means no offset column had BOTH surfaces, so the
 * line's areas are structurally 0 rather than measured as 0 (and a stack's
 * lines carry geometry but no end areas at all). That is `null` here, which
 * the caller prints as an em dash — never a zero (global constraint).
 */
export function chipAreas(
  line: { cutAreaM2: number; fillAreaM2: number; valid: boolean } | null,
  bin: { cutAreaM2: number; fillAreaM2: number } | null,
): SectionChipAreas {
  if (line) return line.valid ? { cutM2: line.cutAreaM2, fillM2: line.fillAreaM2 } : { cutM2: null, fillM2: null };
  if (bin) return { cutM2: bin.cutAreaM2, fillM2: bin.fillAreaM2 };
  return { cutM2: null, fillM2: null };
}

/**
 * Camera pose for "Look at section" (spec §11.3): stand up-station on the
 * tangent and look back at the slab, so the section is seen face-on rather
 * than as a line. Lifted slightly off the horizontal — a perfectly edge-on
 * view of a vertical slab reads as nothing at all.
 *
 * WORLD coordinates, not floating-origin relative: the camera lives in world
 * space.
 */
export function lookAtSectionPose(
  alignment: Alignment,
  station: number,
  widthM: number,
  heightM: number,
): { position: [number, number, number]; target: [number, number, number] } {
  const p = pointAtStation(alignment, station);
  const d = Math.max(widthM, heightM, 1) * LOOK_AT_FRAMING;
  return {
    position: [p.x + p.tx * d, p.y + p.ty * d, p.z + d * LOOK_AT_LIFT],
    target: [p.x, p.y, p.z],
  };
}
